import React, { useState } from "react";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

export default function HubMedLinkingPanel() {
  const [seedTerm, setSeedTerm] = useState("");
  const [hubmedTerm, setHubmedTerm] = useState("");
  const [haloTerm, setHaloTerm] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const [hubmedLoading, setHubmedLoading] = useState(false);
  const [haloLoading, setHaloLoading] = useState(false);
  const [hubmedApiError, setHubmedApiError] = useState(null);
  const [haloApiError, setHaloApiError] = useState(null);

  const [halaxyOrgs, setHalaxyOrgs] = useState([]);
  const [chemists, setChemists] = useState([]);

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedChemist, setSelectedChemist] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkDone, setLinkDone] = useState(false);
  const [linkMessage, setLinkMessage] = useState(null);

  const searchHubmed = async (term) => {
    const word = term.trim();
    if (!word) return;
    setHubmedLoading(true);
    setHubmedApiError(null);
    setSelectedOrg(null);
    setLinkDone(false);
    setLinkMessage(null);
    try {
      const data = await phappsRequest(API_PATHS.SEARCH_HALAXY_FOR_CHEMIST, "POST", {
        HubmedSearch: word,
      });
      setHalaxyOrgs(Array.isArray(data.halaxyOrganizations) ? data.halaxyOrganizations : []);
      if (data.halaxyError) setHubmedApiError(data.halaxyError);
    } catch (e) {
      setHubmedApiError({ message: e.message || "HubMed search failed." });
      setHalaxyOrgs([]);
    } finally {
      setHubmedLoading(false);
    }
  };

  const searchHalo = async (term) => {
    const word = term.trim();
    if (!word) return;
    setHaloLoading(true);
    setHaloApiError(null);
    setSelectedChemist(null);
    setLinkDone(false);
    setLinkMessage(null);
    try {
      const data = await phappsRequest(API_PATHS.SEARCH_HALAXY_FOR_CHEMIST, "POST", {
        HaloSearch: word,
      });
      setChemists(Array.isArray(data.chemists) ? data.chemists : []);
    } catch (e) {
      setHaloApiError({ message: e.message || "Pharmacy Halo search failed." });
      setChemists([]);
    } finally {
      setHaloLoading(false);
    }
  };

  const runSeedSearch = async () => {
    const word = seedTerm.trim();
    if (!word) return;
    setHubmedTerm(word);
    setHaloTerm(word);
    setHasSearched(true);
    setSelectedOrg(null);
    setSelectedChemist(null);
    setLinkDone(false);
    setLinkMessage(null);
    setHubmedApiError(null);
    setHaloApiError(null);

    setHubmedLoading(true);
    setHaloLoading(true);
    try {
      const data = await phappsRequest(API_PATHS.SEARCH_HALAXY_FOR_CHEMIST, "POST", {
        HubmedSearch: word,
        HaloSearch: word,
      });
      setHalaxyOrgs(Array.isArray(data.halaxyOrganizations) ? data.halaxyOrganizations : []);
      setChemists(Array.isArray(data.chemists) ? data.chemists : []);
      if (data.halaxyError) setHubmedApiError(data.halaxyError);
    } catch (e) {
      setHubmedApiError({ message: e.message || "Search failed." });
      setHaloApiError({ message: e.message || "Search failed." });
      setHalaxyOrgs([]);
      setChemists([]);
    } finally {
      setHubmedLoading(false);
      setHaloLoading(false);
    }
  };

  const runLink = async () => {
    if (!selectedOrg || !selectedChemist || linkDone) return;
    setLinkLoading(true);
    setLinkMessage(null);
    try {
      const res = await phappsRequest(API_PATHS.LINK_HUBMED_CLINIC, "POST", {
        ClientID: selectedOrg.ClientID,
        ChemistID: selectedChemist.ChemistID,
      });
      if (res && res.success === true) {
        setLinkDone(true);
        setLinkMessage(
          res.alreadyLinked
            ? "These records were already linked. Open the Pricing settings page and use Edit pricing for this chemist."
            : "Linked successfully. UseBulkBilled has been enabled. Open the Pricing settings page to set DocLink pricing for this chemist."
        );
      } else {
        setLinkMessage({
          type: "err",
          text: res?.comment || "Link could not be completed.",
        });
      }
    } catch (e) {
      setLinkMessage({ type: "err", text: e.message || "Link failed." });
    } finally {
      setLinkLoading(false);
    }
  };

  const canMatch = !!selectedOrg && !!selectedChemist && !linkDone && !linkLoading;
  const anyLoading = hubmedLoading || haloLoading;

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-600">
        Enter a search term to find the chemist in both systems. HubMed uses a{" "}
        <em>starts-with</em> search, so you can refine each box independently if
        the name differs between systems. Then select one from each column and
        click <strong>Match</strong>.
      </p>

      {/* Seed search */}
      <section className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-3">
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          Search both systems
        </h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={seedTerm}
            onChange={(e) => setSeedTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSeedSearch()}
            placeholder="e.g. Blooms, Chemist Warehouse…"
            className="flex-1 rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
            disabled={anyLoading}
          />
          <button
            type="button"
            onClick={runSeedSearch}
            disabled={anyLoading || !seedTerm.trim()}
            className="px-4 py-2.5 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50"
          >
            {anyLoading ? "Searching…" : "Search"}
          </button>
        </div>
      </section>

      {/* Results columns — visible after first search */}
      {hasSearched && (
        <div className="grid md:grid-cols-2 gap-4">
          {/* HubMed column */}
          <section className="rounded-xl border border-slate-200 p-4 flex flex-col gap-3">
            <h3 className="text-xs font-semibold text-violet-900 uppercase tracking-wide">
              HubMed client
            </h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={hubmedTerm}
                onChange={(e) => setHubmedTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchHubmed(hubmedTerm)}
                placeholder="HubMed search (starts with)…"
                className="flex-1 min-w-0 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                disabled={hubmedLoading}
              />
              <button
                type="button"
                onClick={() => searchHubmed(hubmedTerm)}
                disabled={hubmedLoading || !hubmedTerm.trim()}
                className="px-3 py-2 rounded-lg bg-violet-100 text-violet-800 text-sm font-semibold hover:bg-violet-200 disabled:opacity-50 whitespace-nowrap"
              >
                {hubmedLoading ? "…" : "Search"}
              </button>
            </div>
            {hubmedApiError && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                {String(hubmedApiError.message || hubmedApiError)}
              </p>
            )}
            <ul className="space-y-2 overflow-y-auto max-h-72">
              {halaxyOrgs.length === 0 && !hubmedLoading && (
                <li className="text-sm text-slate-400 py-4 text-center">No results.</li>
              )}
              {halaxyOrgs.map((org) => {
                const active = selectedOrg?.ClientID === org.ClientID;
                return (
                  <li key={org.ClientID}>
                    <button
                      type="button"
                      onClick={() => setSelectedOrg(active ? null : org)}
                      className={`w-full text-left rounded-lg px-3 py-2.5 text-sm font-medium border transition ${
                        active
                          ? "border-violet-500 bg-violet-50 text-violet-900"
                          : "border-slate-100 bg-white text-slate-800 hover:border-slate-200"
                      }`}
                    >
                      <span className="block">{org.ChemistName}</span>
                      <span className="block text-xs text-slate-400 mt-0.5">{org.ClientID}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          {/* Pharmacy Halo column */}
          <section className="rounded-xl border border-slate-200 p-4 flex flex-col gap-3">
            <h3 className="text-xs font-semibold text-teal-900 uppercase tracking-wide">
              Pharmacy Halo chemist
            </h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={haloTerm}
                onChange={(e) => setHaloTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchHalo(haloTerm)}
                placeholder="PH search (contains)…"
                className="flex-1 min-w-0 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                disabled={haloLoading}
              />
              <button
                type="button"
                onClick={() => searchHalo(haloTerm)}
                disabled={haloLoading || !haloTerm.trim()}
                className="px-3 py-2 rounded-lg bg-teal-100 text-teal-800 text-sm font-semibold hover:bg-teal-200 disabled:opacity-50 whitespace-nowrap"
              >
                {haloLoading ? "…" : "Search"}
              </button>
            </div>
            {haloApiError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {String(haloApiError.message || haloApiError)}
              </p>
            )}
            <ul className="space-y-2 overflow-y-auto max-h-72">
              {chemists.length === 0 && !haloLoading && (
                <li className="text-sm text-slate-400 py-4 text-center">No results.</li>
              )}
              {chemists.map((c) => {
                const active = selectedChemist?.ChemistID === c.ChemistID;
                return (
                  <li key={c.ChemistID}>
                    <button
                      type="button"
                      onClick={() => setSelectedChemist(active ? null : c)}
                      className={`w-full text-left rounded-lg px-3 py-2.5 text-sm font-medium border transition ${
                        active
                          ? "border-teal-500 bg-teal-50 text-teal-900"
                          : "border-slate-100 bg-white text-slate-800 hover:border-slate-200"
                      }`}
                    >
                      <span className="block">{c.ChemistName}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      )}

      {/* Match row — visible after first search */}
      {hasSearched && (
        <div className="flex flex-col items-center gap-3">
          {(selectedOrg || selectedChemist) && (
            <p className="text-xs text-slate-500 text-center">
              {selectedOrg ? (
                <span className="text-violet-700 font-medium">{selectedOrg.ChemistName}</span>
              ) : (
                <span className="text-slate-400">No HubMed selection</span>
              )}
              {" "}→{" "}
              {selectedChemist ? (
                <span className="text-teal-700 font-medium">{selectedChemist.ChemistName}</span>
              ) : (
                <span className="text-slate-400">No PH selection</span>
              )}
            </p>
          )}
          <button
            type="button"
            onClick={runLink}
            disabled={!canMatch}
            className="px-8 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-teal-600 text-white text-sm font-semibold shadow-md disabled:opacity-40 disabled:pointer-events-none"
          >
            {linkLoading ? "Matching…" : "Match"}
          </button>
          {linkMessage && (
            <div
              className={`text-sm rounded-lg px-4 py-2 max-w-lg text-center ${
                typeof linkMessage === "object" && linkMessage.type === "err"
                  ? "bg-red-50 text-red-700 border border-red-100"
                  : "bg-emerald-50 text-emerald-800 border border-emerald-100"
              }`}
            >
              {typeof linkMessage === "string" ? linkMessage : linkMessage.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
